/**
 * Jersey Lab · Carrito
 *  - <cart-countdown>: cuenta regresiva diaria hasta las 12:00 AM (se reinicia sola cada día).
 *  - <cart-upsell>: "También puede gustarte" con jerseys vistos recientemente + recomendados.
 *
 * Ambos elementos usan `data-skip-subtree-update` en el HTML para que el morph del
 * drawer no borre lo que este script pinta cuando el carrito se actualiza.
 */

const DEFAULT_TZ = 'America/Monterrey';
const VIEWED_KEY = 'jl_viewed_handles';
const VIEWED_MAX = 8;

/* ------------------------------------------------------------------ */
/* Utilidades                                                          */
/* ------------------------------------------------------------------ */

const root = () => window.Shopify?.routes?.root || '/';

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 */
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

async function fetchJson(url) {
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/** Segundos que faltan para las 12:00 AM en la zona horaria indicada. */
function secondsUntilMidnight(timeZone) {
  let elapsed;
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date());
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    elapsed = (get('hour') % 24) * 3600 + get('minute') * 60 + get('second');
  } catch {
    const now = new Date();
    elapsed = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  }
  return Math.min(86399, 86400 - elapsed);
}

/* ------------------------------------------------------------------ */
/* Contador diario                                                     */
/* ------------------------------------------------------------------ */

class CartCountdown extends HTMLElement {
  /** @type {number | null} */
  #interval = null;

  connectedCallback() {
    this.setAttribute('role', 'timer');
    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    if (this.#interval != null) window.clearInterval(this.#interval);
    this.#interval = null;
  }

  #tick() {
    const left = secondsUntilMidnight(this.dataset.timezone || DEFAULT_TZ);
    const h = Math.floor(left / 3600);
    const m = Math.floor((left % 3600) / 60);
    const s = left % 60;
    this.textContent = [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
  }
}

if (!customElements.get('cart-countdown')) {
  customElements.define('cart-countdown', CartCountdown);
}

/* ------------------------------------------------------------------ */
/* Productos vistos recientemente                                      */
/* ------------------------------------------------------------------ */

function readViewed() {
  try {
    const value = JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]');
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function trackViewedProduct() {
  const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  if (!match?.[1]) return;
  try {
    const handle = decodeURIComponent(match[1]);
    const list = readViewed().filter((h) => h !== handle);
    list.unshift(handle);
    localStorage.setItem(VIEWED_KEY, JSON.stringify(list.slice(0, VIEWED_MAX)));
  } catch {
    /* almacenamiento bloqueado: se ignora */
  }
}

trackViewedProduct();

/* ------------------------------------------------------------------ */
/* Datos de producto                                                   */
/* ------------------------------------------------------------------ */

/** Convierte precios de la API (centavos numéricos o strings "897.00") a centavos. */
function toCents(value) {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Math.round(value);
  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

/**
 * @typedef {{ id: number, available: boolean, price: number, compare: number, options: string[] }} Variant
 * @typedef {{ id: number, handle: string, title: string, image: string, price: number, compare: number, optionNames: string[], variants: Variant[] }} Product
 */

/** @returns {Product} */
function normalize(raw) {
  /** @type {Variant[]} */
  const variants = (raw.variants || []).map((v) => ({
    id: v.id,
    available: v.available !== false,
    price: toCents(v.price),
    compare: toCents(v.compare_at_price),
    options: v.options ?? [v.option1, v.option2, v.option3].filter((o) => o != null),
  }));

  const available = variants.filter((v) => v.available);
  const base = [...(available.length ? available : variants)].sort((a, b) => a.price - b.price)[0];

  const firstImage = raw.images?.[0];
  const image =
    (typeof raw.featured_image === 'string' ? raw.featured_image : raw.featured_image?.src) ||
    (typeof firstImage === 'string' ? firstImage : firstImage?.src) ||
    '';

  return {
    id: raw.id,
    handle: raw.handle,
    title: raw.title,
    image,
    price: base?.price ?? toCents(raw.price),
    compare: base?.compare ?? toCents(raw.compare_at_price),
    optionNames: (raw.options || []).map((o) => (typeof o === 'string' ? o : o.name)),
    variants,
  };
}

function sized(src, width = 360) {
  if (!src) return '';
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return src;
  }
}

async function fetchRecent() {
  const handles = readViewed().slice(0, 6);
  const settled = await Promise.allSettled(
    handles.map((handle) => fetchJson(`${root()}products/${encodeURIComponent(handle)}.js`))
  );
  return settled.filter((r) => r.status === 'fulfilled').map((r) => r.value);
}

async function fetchRelated(seedId) {
  if (!seedId) return [];
  const data = await fetchJson(`${root()}recommendations/products.json?product_id=${seedId}&limit=10&intent=related`);
  return data.products ?? [];
}

async function fetchFallback() {
  const data = await fetchJson(`${root()}collections/all/products.json?limit=12`);
  return data.products ?? [];
}

/** Alterna elementos de dos listas: a0, b0, a1, b1... */
function interleave(a, b) {
  const out = [];
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    if (a[i]) out.push(a[i]);
    if (b[i]) out.push(b[i]);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Agregar al carrito                                                  */
/* ------------------------------------------------------------------ */

async function addToCart(variantId) {
  const actions = window.Shopify?.actions;

  if (actions?.updateCart) {
    const result = await actions.updateCart(
      { lines: [{ merchandiseId: String(variantId), quantity: 1 }] },
      { event: { context: 'dialog' } }
    );
    const error = result?.userErrors?.[0];
    if (error) throw new Error(error.message);
    return;
  }

  // Respaldo si la API del tema no está disponible
  const response = await fetch(`${root()}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: 'cart-drawer-section',
      sections_url: window.location.pathname,
    }),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'No se pudo agregar');

  const { morphSection, sectionRenderer } = await import('@theme/section-renderer');
  const html = data.sections?.['cart-drawer-section'];
  if (html) {
    await morphSection('cart-drawer-section', html, { mode: 'hydration', injectStylesheet: true });
  } else {
    await sectionRenderer.renderSection('cart-drawer-section', { cache: false, mode: 'hydration' });
  }
}

/* ------------------------------------------------------------------ */
/* También puede gustarte                                              */
/* ------------------------------------------------------------------ */

class CartUpsell extends HTMLElement {
  static observedAttributes = ['data-cart-ids', 'data-seed', 'data-pill'];

  /** @type {Product[]} */
  #pool = [];
  #loaded = false;
  #loading = false;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {Intl.NumberFormat | null} */
  #formatter = null;

  connectedCallback() {
    if (!this.#loaded && !this.hasChildNodes()) this.#renderSkeleton();

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#load();
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.#loaded) return;
    if (name === 'data-seed') {
      this.#loaded = false;
      this.#load();
    } else {
      this.#render();
    }
  }

  /** @param {number} cents */
  #money(cents) {
    this.#formatter ??= new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: this.dataset.currency || 'MXN',
      currencyDisplay: 'narrowSymbol',
    });
    return this.#formatter.format(cents / 100);
  }

  async #load() {
    if (this.#loading) return;
    this.#loading = true;
    try {
      const [recent, related] = await Promise.all([
        fetchRecent().catch(() => []),
        fetchRelated(this.dataset.seed).catch(() => []),
      ]);

      let raw = interleave(recent, related);
      if (raw.length < 6) raw = raw.concat(await fetchFallback().catch(() => []));

      const seen = new Set();
      this.#pool = raw
        .map(normalize)
        .filter((p) => {
          if (!p.id || seen.has(p.id)) return false;
          seen.add(p.id);
          return p.variants.some((v) => v.available);
        });

      this.#loaded = true;
      this.#render();
    } catch (error) {
      console.warn('[cart-upsell]', error);
      this.replaceChildren();
    } finally {
      this.#loading = false;
    }
  }

  #header() {
    const frag = document.createDocumentFragment();
    const head = el('div', 'cart-upsell__header');
    head.append(el('h3', 'cart-upsell__title', 'TAMBIÉN PUEDE GUSTARTE'));
    const pill = this.dataset.pill;
    if (pill) head.append(el('span', 'cart-upsell__hint', pill));
    frag.append(head, el('p', 'cart-upsell__subtitle', 'Van con lo que ya llevas en el carrito'));
    return frag;
  }

  #renderSkeleton() {
    const list = el('div', 'cart-upsell__list cart-upsell__list--loading');
    for (let i = 0; i < 3; i++) {
      const card = el('div', 'cart-upsell__card cart-upsell__card--skeleton');
      card.append(el('div', 'cart-upsell__media'), el('div', 'cart-upsell__skeleton-line'));
      list.append(card);
    }
    this.replaceChildren(this.#header(), list);
  }

  #render() {
    const inCart = new Set((this.dataset.cartIds || '').split(',').filter(Boolean));
    const limit = Number(this.dataset.limit) || 8;
    const items = this.#pool.filter((p) => !inCart.has(String(p.id))).slice(0, limit);

    if (!items.length) {
      this.replaceChildren();
      return;
    }

    const list = el('div', 'cart-upsell__list');
    for (const product of items) list.append(this.#card(product));
    this.replaceChildren(this.#header(), list);
  }

  /** @param {Product} product */
  #card(product) {
    const card = el('article', 'cart-upsell__card');
    const href = `${root()}products/${product.handle}`;

    const media = el('a', 'cart-upsell__media');
    media.href = href;
    media.setAttribute('aria-label', product.title);
    if (product.image) {
      const img = document.createElement('img');
      img.src = sized(product.image);
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      media.append(img);
    }
    if (product.compare > product.price) {
      const off = Math.round(((product.compare - product.price) / product.compare) * 100);
      if (off > 0) media.append(el('span', 'cart-upsell__badge', `-${off}%`));
    }

    const title = el('a', 'cart-upsell__name', product.title);
    title.href = href;

    const prices = el('div', 'cart-upsell__prices');
    prices.append(el('strong', 'cart-upsell__price', this.#money(product.price)));
    if (product.compare > product.price) prices.append(el('s', 'cart-upsell__compare', this.#money(product.compare)));

    const button = el('button', 'cart-upsell__add', '+ Agregar');
    button.type = 'button';
    button.addEventListener('click', () => this.#onAdd(product, button, card));

    card.append(media, title, prices, button);
    return card;
  }

  /**
   * @param {Product} product
   * @param {HTMLButtonElement} button
   */
  #onAdd(product, button) {
    const hasRealOptions =
      product.variants.length > 1 && !(product.optionNames.length === 1 && product.optionNames[0] === 'Title');

    if (!hasRealOptions) {
      const variant = product.variants.find((v) => v.available) ?? product.variants[0];
      if (variant) this.#addVariant(variant.id, button, '+ Agregar');
      return;
    }
    this.#openSheet(product);
  }

  /**
   * @param {number} variantId
   * @param {HTMLButtonElement} button
   * @param {string} idleLabel
   */
  async #addVariant(variantId, button, idleLabel) {
    button.disabled = true;
    button.textContent = 'Agregando…';
    try {
      await addToCart(variantId);
      button.textContent = '✓ Agregado';
      this.#closeSheet();
    } catch (error) {
      console.warn('[cart-upsell] add failed', error);
      button.disabled = false;
      button.textContent = 'Reintentar';
      window.setTimeout(() => {
        if (button.isConnected && !button.disabled) button.textContent = idleLabel;
      }, 2500);
    }
  }

  #closeSheet() {
    this.querySelector('.cart-upsell__sheet')?.remove();
  }

  /** @param {Product} product */
  #openSheet(product) {
    this.#closeSheet();

    const start = product.variants.find((v) => v.available) ?? product.variants[0];
    const selected = [...start.options];

    const sheet = el('div', 'cart-upsell__sheet');
    sheet.setAttribute('role', 'group');
    sheet.setAttribute('aria-label', `Elige opciones de ${product.title}`);

    const head = el('div', 'cart-upsell__sheet-head');
    head.append(el('strong', 'cart-upsell__sheet-title', product.title));
    const close = el('button', 'cart-upsell__sheet-close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Cerrar');
    close.addEventListener('click', () => this.#closeSheet());
    head.append(close);

    const price = el('div', 'cart-upsell__sheet-price');
    const confirm = el('button', 'cart-upsell__add cart-upsell__add--confirm', 'AGREGAR AL CARRITO');
    confirm.type = 'button';

    const findVariant = () => product.variants.find((v) => v.options.every((o, i) => o === selected[i]));

    /** @type {HTMLSelectElement[]} */
    const selects = [];
    const fields = product.optionNames.map((name, index) => {
      const label = el('label', 'cart-upsell__field');
      label.append(el('span', 'cart-upsell__field-name', name));
      const select = document.createElement('select');
      select.className = 'cart-upsell__select';
      select.addEventListener('change', () => {
        selected[index] = select.value;
        refresh();
      });
      selects.push(select);
      label.append(select);
      return label;
    });

    const refresh = () => {
      selects.forEach((select, index) => {
        const values = [...new Set(product.variants.map((v) => v.options[index]))];
        select.replaceChildren(
          ...values.map((value) => {
            const probe = [...selected];
            probe[index] = value;
            const match = product.variants.find((v) => v.options.every((o, i) => o === probe[i]));
            const option = document.createElement('option');
            option.value = value;
            option.textContent = match && match.available ? value : `${value} · agotado`;
            option.selected = value === selected[index];
            return option;
          })
        );
      });

      const variant = findVariant();
      if (variant?.available) {
        price.textContent = this.#money(variant.price);
        confirm.disabled = false;
        confirm.textContent = 'AGREGAR AL CARRITO';
      } else {
        price.textContent = variant ? this.#money(variant.price) : '';
        confirm.disabled = true;
        confirm.textContent = variant ? 'AGOTADO' : 'NO DISPONIBLE';
      }
    };

    confirm.addEventListener('click', () => {
      const variant = findVariant();
      if (variant?.available) this.#addVariant(variant.id, confirm, 'AGREGAR AL CARRITO');
    });

    sheet.append(head, price, ...fields, confirm);
    refresh();

    this.querySelector('.cart-upsell__list')?.after(sheet);
    sheet.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
